import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { merchantApi } from '../api/config';
import { Order } from '../types';
import { fmtMoney } from '../utils/formatMoney';
import { slipMismatchInfo } from '../utils/slipMismatch';
import { toast } from 'react-toastify';
import socketService from '../services/socketService';
import soundService from '../services/soundService';
import { useAuth } from '../contexts/AuthContext';
import { useOrderNotification } from '../hooks/useOrderNotification';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import bankLogos from 'thai-banks-logo/banks-logo.json';
import { faWpforms } from '@fortawesome/free-brands-svg-icons';
import {
  faHourglass, faCheckCircle, faTimesCircle, faBan, faLink, faRotate,
  faUser, faCalendar, faPaperclip, faCreditCard, faInbox,
  faClipboardList, faSearch, faDownload, faCamera, faUpload, faExclamationTriangle,
  faEye, faBell,
} from '@fortawesome/free-solid-svg-icons';
import { Tooltip } from 'react-tooltip';
import DataTable, { Column } from '../components/shared/DataTable';
import { SlipAttemptTimeline, slipAttemptUrls } from '../components/shared';
import { swalConfirm } from '../components/shared/swalConfirm';
import {
  UiPageHeader, UiFilterPanel, UiButton, UiModal, UiInput, UiSelect, StatusBadge,
} from '../components/ui';
import type { StatusDef } from '../components/ui';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = 'Asia/Bangkok';

const ORDER_STATUS_MAP: Record<string, StatusDef> = {
  pending:   { bg: 'bg-amber-100', text: 'text-amber-700', icon: faHourglass, label: 'Pending' },
  paid:      { bg: 'bg-green-100', text: 'text-green-700', icon: faCheckCircle, label: 'Paid' },
  expired:   { bg: 'bg-red-100', text: 'text-red-600', icon: faTimesCircle, label: 'Expired' },
  cancelled: { bg: 'bg-gray-100', text: 'text-gray-600', icon: faBan, label: 'Cancelled' },
};

const OrdersPage: React.FC = () => {
  const { t } = useTranslation();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Date range filter state (ค่าเริ่มต้น = วันนี้) — draft vs applied
  const [dateFrom, setDateFrom] = useState(() => dayjs().tz(TZ).format('YYYY-MM-DD'));
  const [dateTo, setDateTo] = useState(() => dayjs().tz(TZ).format('YYYY-MM-DD'));
  const [appliedDateFrom, setAppliedDateFrom] = useState(() => dayjs().tz(TZ).format('YYYY-MM-DD'));
  const [appliedDateTo, setAppliedDateTo] = useState(() => dayjs().tz(TZ).format('YYYY-MM-DD'));

  // Server-side pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  // Slip upload (strict verification — ไม่มี bypass)
  const [showSlipUpload, setShowSlipUpload] = useState(false);
  const [slipFile, setSlipFile] = useState<File | null>(null);
  const [slipPreview, setSlipPreview] = useState<string>('');
  const [uploadingSlip, setUploadingSlip] = useState(false);
  const [slipError, setSlipError] = useState<string | null>(null);

  const { merchant } = useAuth();
  const [currentTime, setCurrentTime] = useState(Date.now());
  const isEmployee = merchant?.role === 'employee' || (merchant as any)?.type === 'employee';
  const canSendCallback = !isEmployee || (merchant as any)?.permissions?.can_send_callback !== false;
  const { notifyNewOrder, notifyPaid } = useOrderNotification();

  // Load orders function with server-side pagination
  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      const offset = (currentPage - 1) * pageSize;
      let url = `/merchants/orders?limit=${pageSize}&offset=${offset}`;

      if (statusFilter) url += `&status=${statusFilter}`;

      // ถ้ามี searchQuery → ค้นหาแบบไม่อ้างอิงวันที่
      if (searchQuery.trim()) {
        url += `&search=${encodeURIComponent(searchQuery.trim())}`;
      } else {
        // ถ้าไม่มี search → ใช้ date filter
        if (appliedDateFrom) url += `&date_from=${appliedDateFrom}`;
        if (appliedDateTo) url += `&date_to=${appliedDateTo}`;
      }

      const response = await merchantApi.get(url);
      setOrders(response.data.data);
      setTotalItems(response.data.pagination?.total || 0);
    } catch (error: any) {
      toast.error(t('common.cannotLoadData'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchQuery, currentPage, pageSize, appliedDateFrom, appliedDateTo]);

  const loadOrdersRef = useRef(loadOrders);
  useEffect(() => {
    loadOrdersRef.current = loadOrders;
  }, [loadOrders]);

  // Handle date search button click — apply date draft
  const handleDateSearch = () => {
    if (dayjs(dateFrom).isAfter(dayjs(dateTo), 'day')) {
      toast.error(t('order.dateFromError'));
      return;
    }
    setAppliedDateFrom(dateFrom);
    setAppliedDateTo(dateTo);
    setSearchQuery('');
    setCurrentPage(1);
  };

  const handleReset = () => {
    const today = dayjs().tz(TZ).format('YYYY-MM-DD');
    setDateFrom(today);
    setDateTo(today);
    setAppliedDateFrom(today);
    setAppliedDateTo(today);
    setSearchQuery('');
    setStatusFilter('');
    setCurrentPage(1);
  };

  const [exporting, setExporting] = useState(false);

  const exportCSV = async () => {
    try {
      setExporting(true);

      let url = `/merchants/orders/export`;
      const params: string[] = [];
      if (statusFilter) params.push(`status=${statusFilter}`);
      if (searchQuery.trim()) {
        params.push(`search=${encodeURIComponent(searchQuery.trim())}`);
      } else {
        if (appliedDateFrom) params.push(`date_from=${appliedDateFrom}`);
        if (appliedDateTo) params.push(`date_to=${appliedDateTo}`);
      }
      if (params.length) url += `?${params.join('&')}`;

      const response = await merchantApi.get(url);
      const rows: Order[] = response.data.data;

      if (!rows.length) {
        toast.warning('ไม่มีข้อมูลสำหรับออกรายงาน');
        return;
      }

      const headers = [
        'Order Number', 'Reference ID', 'Status',
        'Bank', 'Bank Number',
        'Base Amount', 'Fee', 'Net Amount', 'Total Amount',
        'Customer Name', 'Customer Email', 'Customer Phone',
        'Created At', 'Paid At',
      ];

      const escape = (v: any) => {
        const s = v == null ? '' : String(v);
        return s.includes(',') || s.includes('"') || s.includes('\n')
          ? `"${s.replace(/"/g, '""')}"` : s;
      };

      const csvLines = [
        headers.join(','),
        ...rows.map(o => [
          escape(o.order_number),
          escape(o.reference_id || ''),
          escape(o.status),
          escape(o.bank_name || ''),
          escape(o.bank_number || ''),
          escape(o.base_amount),
          escape(o.fee_amount || 0),
          escape(o.net_amount || ''),
          escape(o.total_amount),
          escape(o.customer_name || ''),
          escape(o.customer_email || ''),
          escape((o as any).customer_phone || ''),
          escape(dayjs(o.created_at).tz(TZ).format('DD/MM/YYYY HH:mm:ss')),
          escape(o.paid_at ? dayjs(o.paid_at).tz(TZ).format('DD/MM/YYYY HH:mm:ss') : ''),
        ].join(',')),
      ];

      const bom = '﻿';
      const blob = new Blob([bom + csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `orders_${appliedDateFrom}_${appliedDateTo}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);

      toast.success(`ออกรายงาน ${rows.length} รายการสำเร็จ`);
    } catch {
      toast.error(t('common.cannotLoadData'));
    } finally {
      setExporting(false);
    }
  };

  // Reset to page 1 when filters change
  const prevFiltersRef = useRef({ statusFilter, searchQuery });

  useEffect(() => {
    const prev = prevFiltersRef.current;
    const filtersChanged = prev.statusFilter !== statusFilter || prev.searchQuery !== searchQuery;

    if (filtersChanged) {
      prevFiltersRef.current = { statusFilter, searchQuery };
      if (currentPage !== 1) {
        setCurrentPage(1);
        return;
      }
    }

    loadOrders();
  }, [loadOrders, currentPage, statusFilter, searchQuery]);

  // Update time every second for countdown
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(Date.now());

      const hasJustExpired = orders.some(order => {
        if (order.status !== 'pending') return false;
        const diff = new Date(order.expires_at).getTime() - Date.now();
        return diff > -1000 && diff <= 0;
      });

      if (hasJustExpired) {
        loadOrdersRef.current();
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [orders]);

  // คำนวณเวลาเหลือ (≥ 1 ชม. แสดง h:mm:ss — setting อายุ slip order ตั้งได้ถึง 180 นาที)
  const getTimeRemaining = (expiresAt: string) => {
    const diff = new Date(expiresAt).getTime() - currentTime;
    if (diff <= 0) return '00:00';
    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    const mmss = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    return hours > 0 ? `${hours}:${mmss}` : mmss;
  };

  const isExpired = (expiresAt: string) => new Date(expiresAt).getTime() <= currentTime;

  const copyToClipboard = async (text: string) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        toast.success(t('common.copied'));
        return;
      }
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        if (document.execCommand('copy')) toast.success(t('common.copied'));
        else throw new Error('Copy command failed');
      } catch {
        toast.error(t('common.cannotLoadData'));
      } finally {
        document.body.removeChild(textArea);
      }
    } catch {
      toast.error(t('common.cannotLoadData'));
    }
  };

  // Socket.IO event listeners
  useEffect(() => {
    if (merchant?.id) {
      socketService.connect(merchant.id);

      const handleOrderCreated = (order: any) => {
        notifyNewOrder({ order_number: order.order_number, total_amount: order.total_amount });
        loadOrdersRef.current();
      };

      const handleOrderUpdated = (data: any) => {
        if (data.status === 'paid') {
          notifyPaid({ order_number: data.order_number, amount: data.amount });
        }
        loadOrdersRef.current();
      };

      // ผลตรวจสลิปแบบ async (เคส cache เสีย/ธนาคารดีเลย์ ที่ backend retry เบื้องหลัง)
      // success: จัดการโดย order-updated อยู่แล้ว → toast เฉพาะกรณีล้มเหลวหลัง retry ครบ
      const handleSlipVerified = (data: any) => {
        if (data && data.success === false) {
          toast.error(`${data.order_number}: ${data.message || 'ตรวจสอบสลิปไม่สำเร็จ'}`);
        }
        loadOrdersRef.current();
      };

      soundService.loadSettings();

      socketService.on('order-created', handleOrderCreated);
      socketService.on('order-updated', handleOrderUpdated);
      socketService.on('slip-verified', handleSlipVerified);

      return () => {
        socketService.off('order-created', handleOrderCreated);
        socketService.off('order-updated', handleOrderUpdated);
        socketService.off('slip-verified', handleSlipVerified);
      };
    }
  }, [merchant?.id, notifyNewOrder, notifyPaid]);

  const handleCallback = async (order: Order) => {
    const confirmed = await swalConfirm({
      title: 'ส่ง Callback?',
      html: `ส่ง Callback สำหรับ<br><strong>${order.order_number}</strong><br>อีกครั้ง`,
      confirmText: 'ยืนยัน',
    });
    if (!confirmed) return;
    try {
      const response = await merchantApi.post(`/merchants/webhook/send`, {
        order_id: order.id,
        order_number: order.order_number,
      });
      if (response.data.success) {
        toast.success(t('order.callbackSuccess'));
      } else {
        toast.error(`${t('order.callbackFailed')} ${response?.data?.data?.error || response?.data?.message}`);
      }
    } catch (error: any) {
      const errorMessage = error.response ? error.response.data.message : error.message;
      toast.error(`${t('order.callbackFailed')} ${errorMessage}`);
    }
  };

  // ── Slip upload (strict verification) ──────────────────────
  const handleSlipFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSlipFile(file);
      setSlipError(null);
      const reader = new FileReader();
      reader.onloadend = () => setSlipPreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const openSlipUpload = (order: Order) => {
    setSelectedOrder(order);
    setShowSlipUpload(true);
    setSlipError(null);
    setSlipFile(null);
    setSlipPreview('');
  };

  const uploadSlip = async () => {
    if (!selectedOrder) return;
    if (!slipFile || !slipPreview) { toast.error(t('order.selectSlipFirst')); return; }
    setUploadingSlip(true);
    setSlipError(null);
    try {
      const res = await merchantApi.post(`/orders/${selectedOrder.id}/verify-slip`, { slip_base64: slipPreview });
      // 202 = ธนาคารดีเลย์ ระบบรับเข้าคิวตรวจซ้ำให้ — ยังไม่ paid ห้ามบอกว่าสำเร็จ
      if (res.status === 202 || res.data?.pending) {
        toast.info(res.data?.message || t('order.uploadSlipPending'), { autoClose: 10000 });
      } else {
        toast.success(t('order.uploadSlipSuccess'));
      }
      setShowSlipUpload(false);
      setSlipFile(null);
      setSlipPreview('');
      setSelectedOrder(null);
      loadOrders();
    } catch (error: any) {
      setSlipError(error.response?.data?.message || t('order.uploadSlipError'));
    } finally {
      setUploadingSlip(false);
    }
  };

  // Live countdown for pending (not-yet-expired) orders, else standard badge
  const renderStatus = (order: Order) => {
    if (order.status === 'pending' && order.expires_at && !isExpired(order.expires_at)) {
      return (
        <StatusBadge status="pending" statusMap={ORDER_STATUS_MAP} pulse>
          {getTimeRemaining(order.expires_at)}
        </StatusBadge>
      );
    }
    return <StatusBadge status={order.status} statusMap={ORDER_STATUS_MAP} />;
  };

  // Define columns for DataTable
  const columns: Column<Order>[] = [
    {
      key: 'order_number',
      header: t('order.orderNumber'),
      sortable: true,
      render: (order) => (
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faWpforms} style={{ color: 'var(--theme-primary)' }} className="text-lg" />
            <strong className="text-gray-800 dark:text-slate-100 text-sm">{order.order_number}</strong>
          </div>
          {order.reference_id && (
            <small className="text-gray-500 dark:text-slate-400 ml-6">Ref: {order.reference_id}</small>
          )}
        </div>
      ),
    },
    {
      key: 'bank_info',
      header: t('order.bankColumn'),
      render: (order) => {
        const bankKey = (order.bank_name || '').toUpperCase().replace('CIMBT', 'CIMB').replace('UOBT', 'UOB').replace('LH BANK', 'LHB') as keyof typeof bankLogos;
        const bankInfo = bankLogos[bankKey];
        return (
          <div>
            <div className="flex items-center gap-1.5">
              {bankInfo && <img src={bankInfo.icon} alt={order.bank_name || ''} className="w-6 h-6 rounded-full object-contain" />}
              <span className="font-medium text-gray-700 dark:text-slate-200">{order.bank_name || '-'}</span>
            </div>
            <small className="text-gray-500 dark:text-slate-400">{order.bank_number || '-'}</small>
          </div>
        );
      },
    },
    {
      key: 'base_amount',
      header: t('order.depositAmount'),
      sortable: true,
      render: (order) => (
        <div>
          <div className="text-lg font-bold" style={{ color: 'var(--theme-accent)' }}>
            ฿{order.base_amount.toLocaleString('th-TH', { minimumFractionDigits: 2 })}
          </div>
          <small className="text-gray-400 dark:text-slate-500">{t('order.totalInclude')}: ฿{fmtMoney(order.total_amount)}</small>
          {order.amount_mismatch && (
            <div
              className="mt-1 flex w-fit items-center gap-1 text-[10px] font-semibold text-orange-600 bg-orange-50 dark:bg-orange-900/30 px-1.5 py-0.5 rounded-full cursor-help"
              data-tooltip-id="order-tooltip"
              data-tooltip-content={`ยอดสลิปไม่ตรง: เรียกเก็บ ฿${fmtMoney(order.requested_amount || 0)} → สลิปจ่าย ฿${fmtMoney(order.slip_amount || 0)} (${slipMismatchInfo(order).word} ฿${fmtMoney(slipMismatchInfo(order).absDelta)})`}
            >
              <FontAwesomeIcon icon={faExclamationTriangle} /> {slipMismatchInfo(order).badge}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'fee_amount',
      header: 'Fee',
      render: (order) => (
        order.fee_amount !== undefined && order.fee_amount > 0 ? (
          <span className="text-amber-600 dark:text-amber-400 font-semibold">-฿{fmtMoney(order.fee_amount)}</span>
        ) : (
          <span className="text-gray-400 dark:text-slate-500">-</span>
        )
      ),
    },
    {
      key: 'net_amount',
      header: t('order.netAmount'),
      sortable: true,
      render: (order) => (
        order.net_amount !== undefined && order.net_amount > 0 ? (
          <strong className="text-emerald-600 dark:text-emerald-400 text-lg">
            ฿{order.net_amount.toLocaleString('th-TH', { minimumFractionDigits: 2 })}
          </strong>
        ) : (
          <span className="text-gray-400 dark:text-slate-500">-</span>
        )
      ),
    },
    {
      key: 'customer_name',
      header: t('order.customer'),
      sortable: true,
      render: (order) => (
        <div>
          <div className="font-medium text-gray-700 dark:text-slate-200">{order.customer_name || '-'}</div>
          {order.customer_email && (
            <small className="text-gray-400 dark:text-slate-500">{order.customer_email}</small>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: t('common.status'),
      sortable: true,
      render: (order) => renderStatus(order),
    },
    {
      key: 'payment_url',
      header: 'Payment URL',
      render: (order) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            copyToClipboard(`${window.location.origin}/pay/${order.order_number}`);
          }}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors
                     bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200 hover:bg-gray-200 dark:hover:bg-slate-600"
        >
          <FontAwesomeIcon icon={faLink} /> Copy URL
        </button>
      ),
    },
    {
      key: 'created_at',
      header: t('common.date'),
      sortable: true,
      render: (order) => (
        <div>
          <div className="text-sm text-gray-600 dark:text-slate-300">
            {dayjs(order.created_at).tz(TZ).format('DD/MM/YYYY')}
          </div>
          <div className="text-xs text-gray-400 dark:text-slate-500 mt-0.5">
            {dayjs(order.created_at).tz(TZ).format('HH:mm')}
          </div>
        </div>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (order) => (
        <div className="flex flex-nowrap items-center gap-1.5">
          {canSendCallback && (
            <button
              data-tooltip-id="order-tooltip"
              data-tooltip-content={t('order.tooltipResendCallback')}
              onClick={(e) => { e.stopPropagation(); handleCallback(order); }}
              className="px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors
                         bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50"
            >
              <FontAwesomeIcon icon={faBell} />
            </button>
          )}
          {(order.status === 'pending' || order.status === 'expired') && (
            <button
              data-tooltip-id="order-tooltip"
              data-tooltip-content={t('order.tooltipAttachSlip')}
              onClick={(e) => { e.stopPropagation(); openSlipUpload(order); }}
              className="px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors
                         bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/50"
            >
              <FontAwesomeIcon icon={faPaperclip} />
            </button>
          )}
          <button
            data-tooltip-id="order-tooltip"
            data-tooltip-content={t('order.tooltipViewDetail')}
            onClick={(e) => { e.stopPropagation(); setSelectedOrder(order); }}
            className="px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors bg-blue-100 hover:bg-blue-200 text-blue-600"
          >
            <FontAwesomeIcon icon={faEye} />
          </button>
          <Tooltip
            id="order-tooltip"
            style={{ backgroundColor: '#1f2937', color: '#fff', fontSize: '10px', borderRadius: '6px', zIndex: 100 }}
          />
        </div>
      ),
    },
  ];

  // Table footer for totals (แสดงยอดรวมเฉพาะหน้าปัจจุบัน)
  const tableFooter = (
    <tr>
      <td colSpan={3} className="px-4 py-4">
        <strong className="text-gray-700 dark:text-slate-200">
          {t('common.thisPage')} ({orders.length} {t('common.items')}) • {t('common.allItems')} {totalItems.toLocaleString()} {t('common.items')}
        </strong>
      </td>
      <td className="px-4 py-4">
        <strong className="text-amber-600 dark:text-amber-400">
          -฿{fmtMoney(orders.reduce((sum, o) => sum + (o.fee_amount || 0), 0))}
        </strong>
      </td>
      <td className="px-4 py-4">
        <strong className="text-emerald-600 dark:text-emerald-400 text-lg">
          ฿{orders.reduce((sum, o) => sum + (o.net_amount || o.base_amount || 0), 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}
        </strong>
      </td>
      <td colSpan={5}></td>
    </tr>
  );

  return (
    <div className="w-full">
      <UiPageHeader
        icon={faClipboardList}
        title={t('order.ordersList')}
        subtitle={`${t('order.ordersListDesc')} • ${totalItems.toLocaleString()} ${t('common.items')}`}
      />

      {/* ── Filter (date applies on ค้นหา; search/status apply live) ── */}
      <UiFilterPanel
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFromChange={setDateFrom}
        onDateToChange={setDateTo}
        onSearch={handleDateSearch}
        onReset={handleReset}
        loading={loading}
        actions={
          <>
            <UiButton variant="secondary" icon={faRotate} onClick={loadOrders}>
              {t('common.refresh')}
            </UiButton>
            <UiButton
              variant="secondary"
              icon={exporting ? faRotate : faDownload}
              onClick={exportCSV}
              disabled={exporting || orders.length === 0}
            >
              {exporting ? 'กำลังออกรายงาน...' : 'Export CSV'}
            </UiButton>
          </>
        }
      >
        <UiInput
          wrapperClassName="flex-1 min-w-[200px]"
          label={t('common.search', 'ค้นหา')}
          icon={faSearch}
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && loadOrders()}
          placeholder={t('order.searchPlaceholder')}
        />
        <UiSelect
          wrapperClassName="w-fit min-w-[140px]"
          label={t('common.status')}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">{t('common.all')}</option>
          <option value="pending">Pending</option>
          <option value="paid">Paid</option>
          <option value="expired">Expired</option>
          <option value="cancelled">Cancelled</option>
        </UiSelect>
      </UiFilterPanel>

      {/* ── Orders DataTable ───────────────────────────────────── */}
      <DataTable<Order>
        data={orders}
        columns={columns}
        loading={loading}
        emptyMessage={t('order.noOrders')}
        emptyIcon={<FontAwesomeIcon icon={faInbox} className="text-6xl text-gray-300 dark:text-slate-600" />}
        keyExtractor={(order) => order.id}
        serverPagination={{
          currentPage,
          totalItems,
          pageSize,
          pageSizeOptions: [10, 20, 50, 100, 300, 500],
          onPageChange: (page) => setCurrentPage(page),
          onPageSizeChange: (size) => {
            setPageSize(size);
            setCurrentPage(1);
          },
        }}
        footer={tableFooter}
        hoverable
      />

      {/* ── Order Detail Modal ─────────────────────────────────── */}
      <UiModal
        open={!!selectedOrder && !showSlipUpload}
        onClose={() => setSelectedOrder(null)}
        title={selectedOrder?.order_number || ''}
        icon={faClipboardList}
        iconStyle={{ color: 'var(--theme-primary)' }}
        size="lg"
        scrollable
      >
        {selectedOrder && (
          <div className="space-y-3">
            <div className="flex justify-end">{renderStatus(selectedOrder)}</div>

            {/* Amount tiles */}
            <div>
              <p className="text-[10px] font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-2 px-1">ยอดเงิน</p>
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-3 flex flex-col gap-0.5">
                  <span className="text-[10px] text-gray-400 dark:text-slate-500 font-medium">{t('order.baseAmount')}</span>
                  <span className="text-base font-bold text-blue-700 dark:text-blue-300">฿{fmtMoney(selectedOrder.base_amount)}</span>
                </div>
                <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-3 flex flex-col gap-0.5">
                  <span className="text-[10px] text-gray-400 dark:text-slate-500 font-medium">{t('order.centAdded')}</span>
                  <span className="text-base font-bold text-amber-600 dark:text-amber-400">{selectedOrder.cent_modifier > 0 ? '+' : ''}{fmtMoney(selectedOrder.cent_modifier)}</span>
                </div>
                <div className="rounded-xl p-3 flex flex-col gap-0.5" style={{ background: 'var(--theme-primary-light)' }}>
                  <span className="text-[10px] text-gray-400 dark:text-slate-500 font-medium">{t('order.amountToPay')}</span>
                  <span className="text-base font-bold" style={{ color: 'var(--theme-accent)' }}>฿{fmtMoney(selectedOrder.total_amount)}</span>
                </div>
                {selectedOrder.fee_amount !== undefined && selectedOrder.fee_amount > 0 && (
                  <>
                    <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-3 flex flex-col gap-0.5">
                      <span className="text-[10px] text-gray-400 dark:text-slate-500 font-medium">{t('order.fee')}</span>
                      <span className="text-base font-bold text-red-500 dark:text-red-400">-฿{fmtMoney(selectedOrder.fee_amount)}</span>
                    </div>
                    <div className="col-span-2 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-3 flex flex-col gap-0.5">
                      <span className="text-[10px] text-gray-400 dark:text-slate-500 font-medium">{t('order.netReceived')}</span>
                      <span className="text-lg font-bold text-emerald-600 dark:text-emerald-400">฿{fmtMoney(selectedOrder.net_amount || 0)}</span>
                    </div>
                  </>
                )}
              </div>
              {selectedOrder.amount_mismatch && (
                <div className="mt-2 flex items-start gap-2 rounded-xl bg-orange-50 dark:bg-orange-900/20 px-3 py-2 text-xs text-orange-700 dark:text-orange-300">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-semibold">สลิปยอด{slipMismatchInfo(selectedOrder).word} (ปรับ order ตามยอดสลิป)</div>
                    <div>เรียกเก็บเดิม ฿{fmtMoney(selectedOrder.requested_amount || 0)} → สลิปจ่าย ฿{fmtMoney(selectedOrder.slip_amount || 0)} ({slipMismatchInfo(selectedOrder).word} ฿{fmtMoney(slipMismatchInfo(selectedOrder).absDelta)})</div>
                  </div>
                </div>
              )}
            </div>

            {/* Customer + Dates */}
            <div className="grid grid-cols-2 gap-2">
              {selectedOrder.customer_name && (
                <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-3 space-y-2">
                  <p className="text-[10px] font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wider flex items-center gap-1">
                    <FontAwesomeIcon icon={faUser} className="text-green-500" />
                    {t('order.customerInfo')}
                  </p>
                  <div>
                    <p className="text-[10px] text-gray-400 dark:text-slate-500 mb-0.5">{t('common.name', 'ชื่อ')}</p>
                    <p className="text-sm font-semibold text-gray-800 dark:text-slate-100">{selectedOrder.customer_name}</p>
                  </div>
                  {selectedOrder.customer_email && (
                    <div>
                      <p className="text-[10px] text-gray-400 dark:text-slate-500 mb-0.5">Email</p>
                      <p className="text-xs text-gray-600 dark:text-slate-300 break-all">{selectedOrder.customer_email}</p>
                    </div>
                  )}
                  {selectedOrder.customer_phone && (
                    <div>
                      <p className="text-[10px] text-gray-400 dark:text-slate-500 mb-0.5">{t('common.phone', 'เบอร์โทร')}</p>
                      <p className="text-sm text-gray-700 dark:text-slate-200">{selectedOrder.customer_phone}</p>
                    </div>
                  )}
                </div>
              )}

              <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-3 space-y-2">
                <p className="text-[10px] font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wider flex items-center gap-1">
                  <FontAwesomeIcon icon={faCalendar} className="text-purple-500" />
                  {t('common.date')}
                </p>
                <div>
                  <p className="text-[10px] text-gray-400 dark:text-slate-500 mb-0.5">{t('order.createdAt')}</p>
                  <p className="text-xs font-semibold text-gray-700 dark:text-slate-200">{dayjs(selectedOrder.created_at).tz(TZ).format('DD/MM/YYYY HH:mm:ss')}</p>
                </div>
                {selectedOrder.paid_at && (
                  <div>
                    <p className="text-[10px] text-gray-400 dark:text-slate-500 mb-0.5">{t('order.paidAt')}</p>
                    <p className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">{dayjs(selectedOrder.paid_at).tz(TZ).format('DD/MM/YYYY HH:mm:ss')}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Slip */}
            {selectedOrder.slip_url && (
              <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-3">
                <p className="text-[10px] font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wider flex items-center gap-1 mb-2">
                  <FontAwesomeIcon icon={faPaperclip} className="text-pink-500" />
                  {t('order.uploadedSlip')}
                </p>
                <img
                  src={selectedOrder.slip_url.startsWith('http') || selectedOrder.slip_url.startsWith('data:')
                    ? selectedOrder.slip_url
                    : `data:image/png;base64,${selectedOrder.slip_url}`}
                  alt="Payment Slip"
                  className="max-w-full max-h-80 rounded-lg mx-auto block"
                />
              </div>
            )}

            {/* Slip attempt history */}
            <SlipAttemptTimeline fetchUrl={slipAttemptUrls.merchantOrder(selectedOrder.id)} />

            {/* Transaction Data */}
            {(selectedOrder as any).transaction_data && (
              <div className="bg-gray-900 dark:bg-slate-950 rounded-xl p-3">
                <p className="text-[10px] font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wider flex items-center gap-1 mb-2">
                  <FontAwesomeIcon icon={faCreditCard} className="text-indigo-400" />
                  {t('order.bankRawData')}
                </p>
                <pre className="text-xs text-gray-300 overflow-x-auto max-h-36 overflow-y-auto leading-relaxed">
                  {JSON.stringify((selectedOrder as any).transaction_data, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </UiModal>

      {/* ── Slip Upload Modal (strict verification, no bypass) ── */}
      <UiModal
        open={showSlipUpload && !!selectedOrder}
        onClose={() => { if (!uploadingSlip) { setShowSlipUpload(false); setSelectedOrder(null); } }}
        title={t('order.attachSlipTitle')}
        icon={faPaperclip}
        iconStyle={{ color: 'var(--theme-primary)' }}
        size="md"
      >
        {selectedOrder && (
          <div className="space-y-3">
            <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4 space-y-1 text-sm">
              <p><span className="text-gray-500 dark:text-slate-400">Order:</span> <strong className="dark:text-slate-100">{selectedOrder.order_number}</strong></p>
              <p><span className="text-gray-500 dark:text-slate-400">{t('order.amountDue')}:</span> <strong className="text-lg" style={{ color: 'var(--theme-accent)' }}>฿{fmtMoney(selectedOrder.total_amount)}</strong></p>
              <p className="flex items-center gap-2"><span className="text-gray-500 dark:text-slate-400">{t('common.status')}:</span> {renderStatus(selectedOrder)}</p>
            </div>

            {slipError && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
                <p className="text-sm text-red-700 dark:text-red-400 font-medium flex items-start gap-2">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="mt-0.5" />
                  <span>{slipError}</span>
                </p>
              </div>
            )}

            <label className="block cursor-pointer">
              {slipPreview ? (
                <div className="relative rounded-xl overflow-hidden border-2 border-dashed border-green-300 bg-green-50 dark:bg-green-900/10">
                  <img src={slipPreview} alt="Preview" className="w-full max-h-64 object-contain" />
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                    <span className="text-white text-sm">{t('order.changeImage')}</span>
                  </div>
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-200 dark:border-slate-600 rounded-xl p-8 text-center hover:border-gray-300 dark:hover:border-slate-500 transition-colors">
                  <FontAwesomeIcon icon={faCamera} className="text-4xl text-gray-300 dark:text-slate-600 mb-2" />
                  <p className="text-gray-500 dark:text-slate-400 text-sm">{t('order.selectSlipImage')}</p>
                </div>
              )}
              <input type="file" accept="image/*" onChange={handleSlipFileChange} className="hidden" />
            </label>

            <div>
              <button
                onClick={uploadSlip}
                disabled={!slipFile || uploadingSlip}
                className="w-full py-3 rounded-xl text-white font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: 'var(--theme-primary-gradient)' }}
              >
                <FontAwesomeIcon icon={uploadingSlip ? faHourglass : faUpload} className={uploadingSlip ? 'animate-spin' : ''} />
                {uploadingSlip ? t('order.verifying') : t('order.uploadVerifySlip')}
              </button>
              <p className="text-[11px] text-gray-500 dark:text-slate-400 mt-1.5 text-center">
                {t('order.slipChecksInfo')}
              </p>
            </div>
          </div>
        )}
      </UiModal>
    </div>
  );
};

export default OrdersPage;
